import {
  forwardRef,
  type ReactNode,
  type ElementType,
  useState,
  Children,
  useEffect,
  type MouseEvent,
} from "react";
import { cn } from "@repo/utils";
import NavIconButton from "./nav-icon-button.js";
import { NavTree, NavTreeItem } from "./nav-tree.js";
import type { Sizes } from "../button/button.types.js";

export type ActivePrefixType = (prefix: string, level?: number) => boolean;

export type NavItemProps = {
  to?: string;
  size?: Sizes;
  icon?:
    | ReactNode
    | ((context: {
        isActive: (path: string) => boolean;
        isActivePrefix: ActivePrefixType;
      }) => ReactNode);
  label: string;
  isActive?: boolean;
  onClick?: string | ((e?: MouseEvent) => void);
  className?: string;
  as?: ElementType;
  children?: ReactNode;
  showChevron?: boolean | ((isActivePrefix: ActivePrefixType) => boolean);
  defaultOpen?: boolean | ((isActivePrefix: ActivePrefixType) => boolean);
  isParent?: boolean;
  showTreeLines?: boolean;
};

export const NavItem = forwardRef<HTMLAnchorElement, NavItemProps>(
  (
    {
      to,
      size = "md",
      icon,
      label,
      isActive = false,
      onClick,
      className,
      as: Component = "a",
      children,
      defaultOpen = false,
      showChevron = true,
      isParent = false,
      showTreeLines = true,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(
      typeof defaultOpen === "boolean" ? defaultOpen : false
    );
    const [clickCount, setClickCount] = useState(0);
    const hasChildren = Children.count(children) > 0 || isParent;
    useEffect(() => {
      setIsOpen(typeof defaultOpen === "boolean" ? defaultOpen : false);
    }, [defaultOpen]);

    const toggleOpen = (e?: MouseEvent) => {
      if (children || hasChildren) {
        setIsOpen((prev) => !prev);
      } else if (!to) {
        setClickCount((prev) => prev + 1);
      }
      if (typeof onClick === "function") {
        onClick(e);
      }
    };

    const resolvedIcon =
      typeof icon === "function"
        ? icon({
            isActive: (path: string) => to === path,
            isActivePrefix: (prefix: string) => to?.startsWith(prefix) ?? false,
          })
        : icon;

    return (
      <div>
        <Component tabIndex={-1} ref={ref} to={to} href={to} className={cn(className)}>
          <NavIconButton
            size={size}
            text={label}
            icon={resolvedIcon}
            isActive={isActive}
            isOpen={isOpen}
            showChevron={!!(showChevron && (hasChildren || children))}
            toggleOpen={toggleOpen}
            showEmptyInfo={clickCount >= 5}
            onResetEmptyInfo={() => setClickCount(0)}
          />
        </Component>

        <NavTree size={size} isOpen={isOpen}>
          {Children.map(children, (child, index) => (
            <NavTreeItem key={index} showLines={showTreeLines}>
              {child}
            </NavTreeItem>
          ))}
        </NavTree>
      </div>
    );
  }
);

NavItem.displayName = "NavItem";
